import { useAuthContext } from '../../../context/AuthContext';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminNavLink, adminNavLinkActive } from '../adminStyles';

interface AdminSidebarProps {
  onNavigate?: () => void;
}

type NavItem = {
  href: string;
  label: string;
  icon: React.ReactNode;
  match: (pathname: string) => boolean;
};

const navItems: NavItem[] = [
  {
    href: '/admin',
    label: 'ダッシュボード',
    match: (pathname) => pathname === '/admin' || pathname === '/admin/',
    icon: (
      <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6h16M4 10h16M4 14h10M4 18h6" />
      </svg>
    ),
  },
  {
    href: '/admin/store/tweet',
    label: 'つぶやき',
    match: (pathname) => pathname.startsWith('/admin/store/tweet'),
    icon: (
      <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 8h10M7 12h6m-6 4h8M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-4 3v-3H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
      </svg>
    ),
  },
  {
    href: '/admin/store/report',
    label: 'レポート',
    match: (pathname) => pathname.startsWith('/admin/store/report'),
    icon: (
      <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 19V5m0 14h16M8 15v-3m4 3V9m4 6V7" />
      </svg>
    ),
  },
  {
    href: '/admin/store/plan-items',
    label: 'プラン商品',
    match: (pathname) => pathname.startsWith('/admin/store/plan-items'),
    icon: (
      <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 9 2 2 4-4" />
      </svg>
    ),
  },
  {
    href: '/admin/store/show',
    label: '店舗情報',
    match: (pathname) => pathname.startsWith('/admin/store/show'),
    icon: (
      <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V9.5z" />
      </svg>
    ),
  },
];

const AdminSidebar = ({ onNavigate }: AdminSidebarProps) => {
  const navigator = useNavigate();
  const location = useLocation();
  const { logout } = useAuthContext();

  const handleNavigate = () => {
    onNavigate?.();
  };

  const handleLogout = () => {
    if (!window.confirm('ログアウトしますか？')) {
      return;
    }

    logout();
    navigator('/login');
    onNavigate?.();
  };

  return (
    <aside className="flex h-full flex-col border-r border-stone-200/80 bg-gradient-to-b from-stone-100 via-stone-50/95 to-teal-50/30 py-6 text-stone-800 sm:py-8">
      <div className="mb-6 px-4 sm:mb-8">
        <div className="rounded-2xl border border-teal-100/80 bg-white/80 px-4 py-3 text-center shadow-sm">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-teal-600">Kuraberu</p>
          <h1 className="mt-1 text-lg font-medium text-stone-800">管理メニュー</h1>
        </div>
      </div>

      <nav className="w-full flex-1 px-3">
        <ul className="m-0 list-none space-y-1.5 p-0">
          {navItems.map((item) => {
            const isActive = item.match(location.pathname);
            const linkClass = isActive ? adminNavLinkActive : adminNavLink;

            return (
              <li key={item.href}>
                <a href={item.href} className={linkClass} onClick={handleNavigate}>
                  <span className={isActive ? 'text-teal-500' : 'text-stone-400'}>{item.icon}</span>
                  {item.label}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="mt-4 border-t border-stone-200/80 px-3 pt-4">
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-stone-500 transition-colors hover:bg-white/80 hover:text-stone-700"
          onClick={handleLogout}
        >
          <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12H3m0 0 4-4m-4 4 4 4m6-8V6a2 2 0 0 0-2-2H9m10 8v8a2 2 0 0 1-2 2H9" />
          </svg>
          ログアウト
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;
